import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Collapse, Typography } from '@material-ui/core';
import { purple } from '../../../app/theme';
import ExternalLinkModal from '../ExternalLinkModal/ExternalLinkModal';

const useStyles = makeStyles(() => ({
  list: {
    marginTop: 0,
    marginBottom: 0,
  },
  link: {
    color: purple,
    textTransform: 'none',
    textDecoration: 'underline',
    padding: 0,
    fontSize: 'inherit',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
}));

type IndicationContentProps = {
  isOpen: boolean;
};

const IndicationContent = ({ isOpen }: IndicationContentProps): JSX.Element => {
  const classes = useStyles();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalUrl, setModalUrl] = useState<string>('');

  const handleClickFDAMedWatch = () => {
    setModalUrl('https://www.fda.gov/medwatch');
    setIsModalOpen(true);
  };

  return (
    <>
      <Typography variant="body2">
        <b>INDICATION</b>
      </Typography>
      <Typography paragraph variant="body1">
        Axumin
        <sup>®</sup>
        &nbsp;(fluciclovine F 18) injection is indicated for positron emission tomography (PET) imaging in men with
        suspected prostate cancer recurrence based on elevated blood prostate specific antigen (PSA) levels following
        prior treatment.
      </Typography>
      <Typography variant="body2">
        <b>IMPORTANT SAFETY INFORMATION</b>
      </Typography>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <ul className={classes.list}>
          <li>
            <Typography variant="body1">
              Image interpretation errors can occur with Axumin PET imaging. A negative image does not rule out
              recurrent prostate cancer and a positive image does not confirm its presence. The performance of Axumin
              seems to be affected by PSA levels. Axumin uptake may occur with other cancers and benign prostatic
              hypertrophy in primary prostate cancer. Clinical correlation, which may include histopathological
              evaluation, is recommended.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              Hypersensitivity reactions, including anaphylaxis, may occur in patients who receive Axumin. Emergency
              resuscitation equipment and personnel should be immediately available
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              Axumin use contributes to a patient’s overall long-term cumulative radiation exposure, which is associated
              with an increased risk of cancer. Safe handling practices should be used to minimize radiation exposure to
              the patient and health care providers.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              Adverse reactions were reported in ≤1% of subjects during clinical studies with Axumin. The most common
              adverse reactions were injection site pain, injection site erythema and dysgeusia.
            </Typography>
          </li>
        </ul>
        <Typography paragraph variant="body1">
          To report suspected adverse reactions to Axumin, call 1-855-AXUMIN1 (1-855-298-6461) or contact FDA at
          1-800-FDA-1088 or&nbsp;
          <Button className={classes.link} onClick={handleClickFDAMedWatch}>
            www.fda.gov/medwatch
          </Button>
          .
        </Typography>
        <Typography paragraph variant="body1">
          Please see Axumin&nbsp;
          <a
            className={classes.link}
            href="https://www.axumin.com/prescribing-information.pdf"
            target="_blank"
            rel="noreferrer"
          >
            full Prescribing Information
          </a>
          .
        </Typography>
      </Collapse>
      <ExternalLinkModal
        url={modalUrl}
        isOpen={isModalOpen}
        handleModalOpen={(open: boolean) => setIsModalOpen(open)}
      />
    </>
  );
};

export default IndicationContent;
